import {
  ItemStateSchema,
  type ItemState,
  LinkStatusSchema,
  type LinkStatus,
  RawConditionSchema,
  type RawCondition,
} from "@pokifolio/contracts";

export const LISTING_VIEWS = ["active", "negotiating", "purchased", "closed", "all"] as const;
export type ListingView = (typeof LISTING_VIEWS)[number];
export const LISTING_SORTS = ["latest", "price_asc", "price_desc", "priority"] as const;
export type ListingSort = (typeof LISTING_SORTS)[number];

/** The Listings page's filters, kept in the address so dashboard counts can link straight to them. */
export interface ListingSearch {
  readonly view?: Exclude<ListingView, "active">;
  readonly q?: string;
  readonly status?: LinkStatus;
  readonly condition?: RawCondition;
  readonly itemState?: ItemState;
  readonly platform?: string;
  readonly setName?: string;
  readonly sort?: Exclude<ListingSort, "latest">;
  readonly scope?: "mine";
  readonly page?: number;
}

const oneOf = <T extends string>(values: readonly T[], value: unknown): T | undefined =>
  values.find((v) => v === value);

const text = (value: unknown): string => (typeof value === "string" ? value.trim().slice(0, 100) : "");

export function parseListingSearch(search: Record<string, unknown>): ListingSearch {
  const status = LinkStatusSchema.safeParse(search.status);
  const condition = RawConditionSchema.safeParse(search.condition);
  const itemState = ItemStateSchema.safeParse(search.itemState);
  const view = oneOf(LISTING_VIEWS, search.view);
  const sort = oneOf(LISTING_SORTS, search.sort);
  const q = text(search.q);
  const setName = text(search.setName);
  const page = Number(search.page);
  return {
    ...(view !== undefined && view !== "active" ? { view } : {}),
    ...(q !== "" ? { q } : {}),
    ...(status.success ? { status: status.data } : {}),
    ...(condition.success ? { condition: condition.data } : {}),
    ...(itemState.success ? { itemState: itemState.data } : {}),
    ...(text(search.platform) !== "" ? { platform: text(search.platform) } : {}),
    ...(setName !== "" ? { setName } : {}),
    ...(sort !== undefined && sort !== "latest" ? { sort } : {}),
    ...(search.scope === "mine" ? { scope: "mine" as const } : {}),
    ...(Number.isInteger(page) && page > 1 && page <= 10_000 ? { page } : {}),
  };
}
